import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Search, Download, Users, Check, X, Music, Utensils, MessageCircle, Loader2, RefreshCw, UsersRound, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "mn_admin_pwd";

function authHeaders(pwd) {
  return { "X-Admin-Password": pwd };
}

function LoginGate({ onAuthed }) {
  const [pwd, setPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pwd });
      sessionStorage.setItem(STORAGE_KEY, pwd);
      onAuthed(pwd);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 401 ? "Incorrect password." : "Could not sign in. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6 paper-texture">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Lock className="w-6 h-6 text-[#6A1E2D] mx-auto mb-4" strokeWidth={1.4} />
          <p className="text-[10px] tracking-luxe uppercase text-[#6A1E2D] mb-3">Wedding Admin</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#1A1818] font-light">
            Michelle <span className="italic text-[#6A1E2D]">&amp;</span> Niall
          </h1>
        </div>

        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="bg-[#FAF9F6] border border-[#6A1E2D]/15 p-8 md:p-10 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.18)]"
        >
          <label className="text-[10px] tracking-luxe uppercase text-[#6A1E2D] block mb-3">
            Admin password
          </label>
          <input
            type="password"
            autoFocus
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            data-testid="admin-password-input"
            className="field-underline mb-8"
            placeholder="Enter password"
          />
          <button
            type="submit"
            disabled={submitting || !pwd}
            data-testid="admin-login-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#6A1E2D] hover:bg-[#521723] text-[#FAF9F6] py-3.5 text-xs tracking-luxe uppercase transition-colors disabled:opacity-60"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Signing in</> : "Sign in"}
          </button>
        </form>

        <a
          href="/"
          data-testid="admin-back-home"
          className="block text-center mt-6 text-xs tracking-luxe uppercase text-[#4A4747] hover:text-[#6A1E2D] transition-colors"
        >
          ← Back to wedding site
        </a>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`p-6 md:p-7 border ${accent ? "border-[#6A1E2D]/20 bg-[#6A1E2D] text-[#FAF9F6]" : "border-[#6A1E2D]/15 bg-[#FAF9F6]"}`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`w-4 h-4 ${accent ? "text-[#A3C1DA]" : "text-[#6A1E2D]"}`} strokeWidth={1.4} />
        <span className={`text-[10px] tracking-luxe uppercase ${accent ? "text-[#A3C1DA]" : "text-[#6A1E2D]"}`}>{label}</span>
      </div>
      <p className={`font-display text-4xl md:text-5xl tabular-nums font-light ${accent ? "text-[#FAF9F6]" : "text-[#1A1818]"}`}>
        {value}
      </p>
    </div>
  );
}

function escCSV(v) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

function rsvpToCSVRows(r) {
  const members = Array.isArray(r.party_members) ? r.party_members : [];
  const rows = [];
  // Primary respondent row
  rows.push([
    r.full_name,
    r.email,
    r.attending,
    r.dietary_requirements || "",
    r.guest_count,
    "1 (Primary)",
    r.song_request || "",
    r.message || "",
    r.submitted_at,
  ]);
  // Additional party members
  members.forEach((m, idx) => {
    rows.push([
      m?.name || "",
      r.email,
      r.attending,
      m?.dietary || "",
      r.guest_count,
      String(idx + 2),
      "",
      "",
      r.submitted_at,
    ]);
  });
  return rows.map((row) => row.map(escCSV).join(","));
}

function downloadCSV(rsvps) {
  const headers = [
    "Name", "Email", "Attending", "Dietary",
    "Party Size", "Member Position",
    "Song Request", "Message", "Submitted At",
  ];
  const lines = [headers.map(escCSV).join(",")];
  rsvps.forEach((r) => {
    lines.push(...rsvpToCSVRows(r));
  });
  const csv = lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Dashboard({ pwd, onLogout }) {
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState({ total_rsvps: 0, attending: 0, not_attending: 0, total_guests: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/rsvp`, { headers: authHeaders(pwd) }),
        axios.get(`${API}/rsvp/stats`, { headers: authHeaders(pwd) }),
      ]);
      setRsvps(listRes.data || []);
      setStats(statsRes.data || stats);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load RSVPs.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const handleDelete = async (rsvp) => {
    const confirmed = window.confirm(
      `Delete RSVP from ${rsvp.full_name} (${rsvp.email})?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;
    setDeletingId(rsvp.id);
    try {
      await axios.delete(`${API}/rsvp/${rsvp.id}`, { headers: authHeaders(pwd) });
      toast.success(`Removed ${rsvp.full_name}'s RSVP.`);
      // Optimistically update
      setRsvps((prev) => prev.filter((r) => r.id !== rsvp.id));
      // Refresh stats
      try {
        const statsRes = await axios.get(`${API}/rsvp/stats`, { headers: authHeaders(pwd) });
        setStats(statsRes.data || stats);
      } catch (_) {}
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else if (err?.response?.status === 404) {
        toast.error("That RSVP no longer exists.");
        fetchAll();
      } else {
        toast.error("Could not delete RSVP. Please try again.");
      }
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rsvps.filter((r) => {
      if (filter === "yes" && r.attending !== "yes") return false;
      if (filter === "no" && r.attending !== "no") return false;
      if (!q) return true;
      return (
        (r.full_name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.message || "").toLowerCase().includes(q) ||
        (r.song_request || "").toLowerCase().includes(q)
      );
    });
  }, [rsvps, filter, query]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] paper-texture">
      <header className="bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-[#6A1E2D]/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-luxe uppercase text-[#6A1E2D]">Wedding Admin</p>
            <p className="font-display text-xl md:text-2xl text-[#1A1818]">
              Michelle <span className="italic text-[#6A1E2D]">&amp;</span> Niall · 4 June 2027
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchAll}
              data-testid="admin-refresh-button"
              className="hidden sm:inline-flex items-center gap-2 text-xs tracking-luxe uppercase text-[#1A1818] hover:text-[#6A1E2D] px-4 py-2 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.6} />
              Refresh
            </button>
            <button
              onClick={() => downloadCSV(rsvps)}
              disabled={!rsvps.length}
              data-testid="admin-export-csv-button"
              className="inline-flex items-center gap-2 text-xs tracking-luxe uppercase text-[#FAF9F6] bg-[#6A1E2D] hover:bg-[#521723] disabled:opacity-50 px-4 py-2 transition-colors"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.6} />
              Export CSV
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-xs tracking-luxe uppercase text-[#1A1818] hover:text-[#6A1E2D] px-3 py-2 transition-colors"
              aria-label="Log out"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.6} />
              <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Stats */}
        <section data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
          <StatCard label="Responses" value={stats.total_rsvps} icon={Users} />
          <StatCard label="Attending" value={stats.attending} icon={Check} accent />
          <StatCard label="Not attending" value={stats.not_attending} icon={X} />
          <StatCard label="Total guests" value={stats.total_guests} icon={Users} />
        </section>

        {/* Filters */}
        <section className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between mb-6">
          <div className="flex flex-wrap items-center gap-2" data-testid="admin-filter-buttons">
            {[
              { v: "all", label: "All" },
              { v: "yes", label: "Attending" },
              { v: "no", label: "Not attending" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                data-testid={`admin-filter-${f.v}`}
                className={`px-4 py-2 text-xs tracking-luxe uppercase border transition-colors ${
                  filter === f.v
                    ? "bg-[#6A1E2D] text-[#FAF9F6] border-[#6A1E2D]"
                    : "bg-transparent text-[#1A1818] border-[#6A1E2D]/30 hover:border-[#6A1E2D]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-[#6A1E2D]/60 pointer-events-none z-10" strokeWidth={1.6} />
            <input
              type="search"
              placeholder="Search name, email, message..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-search-input"
              className="field-underline pl-7"
            />
          </div>
        </section>

        {/* Table */}
        <section className="bg-[#FAF9F6] border border-[#6A1E2D]/15">
          {loading ? (
            <div className="p-16 text-center text-[#4A4747]" data-testid="admin-loading">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-3 text-[#6A1E2D]" />
              Loading guest replies…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center" data-testid="admin-empty">
              <p className="font-display text-2xl text-[#1A1818] mb-2">No replies just yet.</p>
              <p className="text-sm text-[#4A4747]">
                {rsvps.length === 0
                  ? "RSVPs will appear here as guests submit them."
                  : "Try clearing your filters or search."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-rsvp-table">
                <thead className="bg-[#F3EFE9]">
                  <tr className="text-left">
                    <th className="px-5 py-4 text-[10px] tracking-luxe uppercase text-[#6A1E2D] font-medium">Guest</th>
                    <th className="px-5 py-4 text-[10px] tracking-luxe uppercase text-[#6A1E2D] font-medium">Status</th>
                    <th className="px-5 py-4 text-[10px] tracking-luxe uppercase text-[#6A1E2D] font-medium">Party</th>
                    <th className="px-5 py-4 text-[10px] tracking-luxe uppercase text-[#6A1E2D] font-medium">Details</th>
                    <th className="px-5 py-4 text-[10px] tracking-luxe uppercase text-[#6A1E2D] font-medium hidden md:table-cell">Submitted</th>
                    <th className="px-5 py-4 text-[10px] tracking-luxe uppercase text-[#6A1E2D] font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`admin-rsvp-row-${r.id}`}
                      className="border-t border-[#6A1E2D]/10 hover:bg-[#F3EFE9]/50 transition-colors align-top"
                    >
                      <td className="px-5 py-5">
                        <p className="font-medium text-[#1A1818]">{r.full_name}</p>
                        <p className="text-xs text-[#4A4747] mt-1">{r.email}</p>
                      </td>
                      <td className="px-5 py-5">
                        {r.attending === "yes" ? (
                          <span className="inline-flex items-center gap-1.5 text-xs tracking-wide text-[#6A1E2D]">
                            <Check className="w-3.5 h-3.5" strokeWidth={2} /> Joining
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs tracking-wide text-[#4A4747]">
                            <X className="w-3.5 h-3.5" strokeWidth={2} /> Regrets
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-5">
                        <span className="font-display text-2xl text-[#1A1818] tabular-nums">{r.guest_count}</span>
                      </td>
                      <td className="px-5 py-5 max-w-md">
                        <div className="space-y-1.5 text-xs text-[#4A4747]">
                          {Array.isArray(r.party_members) && r.party_members.length > 0 && (
                            <div className="flex items-start gap-2">
                              <UsersRound className="w-3.5 h-3.5 mt-0.5 text-[#6A1E2D] shrink-0" strokeWidth={1.6} />
                              <ul className="space-y-0.5">
                                {r.party_members.map((m, i) => (
                                  <li key={i}>
                                    <span className="text-[#1A1818]">{m?.name}</span>
                                    {m?.dietary && (
                                      <span className="text-[#4A4747]/70"> · {m.dietary}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {r.dietary_requirements && (
                            <p className="flex items-start gap-2">
                              <Utensils className="w-3.5 h-3.5 mt-0.5 text-[#6A1E2D] shrink-0" strokeWidth={1.6} />
                              <span>{r.dietary_requirements}</span>
                            </p>
                          )}
                          {r.song_request && (
                            <p className="flex items-start gap-2">
                              <Music className="w-3.5 h-3.5 mt-0.5 text-[#6A1E2D] shrink-0" strokeWidth={1.6} />
                              <span>{r.song_request}</span>
                            </p>
                          )}
                          {r.message && (
                            <p className="flex items-start gap-2">
                              <MessageCircle className="w-3.5 h-3.5 mt-0.5 text-[#6A1E2D] shrink-0" strokeWidth={1.6} />
                              <span className="italic">"{r.message}"</span>
                            </p>
                          )}
                          {!r.dietary_requirements && !r.song_request && !r.message && (
                            <span className="text-[#4A4747]/50">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-5 hidden md:table-cell text-xs text-[#4A4747] tabular-nums whitespace-nowrap">
                        {new Date(r.submitted_at).toLocaleString("en-IE", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-5 py-5 text-right">
                        <button
                          onClick={() => handleDelete(r)}
                          disabled={deletingId === r.id}
                          data-testid={`admin-delete-${r.id}`}
                          aria-label={`Delete RSVP from ${r.full_name}`}
                          className="inline-flex items-center justify-center w-8 h-8 text-[#4A4747] hover:text-[#6A1E2D] hover:bg-[#6A1E2D]/10 transition-colors disabled:opacity-50"
                        >
                          {deletingId === r.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-xs text-[#4A4747] mt-6">
          Showing <span data-testid="admin-result-count">{filtered.length}</span> of {rsvps.length} replies
        </p>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [pwd, setPwd] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPwd("");
  };

  if (!pwd) return <LoginGate onAuthed={setPwd} />;
  return <Dashboard pwd={pwd} onLogout={logout} />;
}
